import { getSubjectLabels } from '@/lib/settings';
import { Textarea } from '../../ui/textarea';
import { WizardCard, WizardKVGrid, WizardKVItem, WizardSection } from '../../ui/wizard-layout';
import { formatDateWithWeekday } from '../../../lib/dateFormat';
import type { FachItem, KursItem } from '../../../lib/courses';
import type { Room } from '../../../lib/rooms';
import type { SelectedTeilnehmerState, TrialRequestPerson } from './types';

type StepSummaryProps = Readonly<{
  fachOptions: FachItem[];
  fachId?: number;
  teacherDisplayName: string;
  date: string;
  startTime: string;
  endTime: string;
  selectedCourseId?: number;
  selectedCourse: KursItem | null;
  selectedStandort?: { name?: string } | null;
  selectedStandortId?: number;
  roomId?: number;
  rooms: Room[];
  formatRoomAddress: (r?: Room | null) => string;
  selectedTeilnehmer: SelectedTeilnehmerState | null;
  trialRequestPerson?: TrialRequestPerson;
  skipStudentEmail: boolean;
  skipTeacherEmail: boolean;
  skipStudentSms: boolean;
  skipTeacherSms: boolean;
  createHistoryText: string;
  onChangeHistoryText: (v: string) => void;
  autoTaskPreview?: { enabled: boolean; description?: string; dueAt?: string } | null;
}>;

const StepSummary = ({
  fachOptions,
  fachId,
  teacherDisplayName,
  date,
  startTime,
  endTime,
  selectedCourseId,
  selectedCourse,
  selectedStandort,
  selectedStandortId,
  roomId,
  rooms,
  formatRoomAddress,
  selectedTeilnehmer,
  trialRequestPerson,
  skipStudentEmail,
  skipTeacherEmail,
  skipStudentSms,
  skipTeacherSms,
  createHistoryText,
  onChangeHistoryText,
  autoTaskPreview,
}: StepSummaryProps) => {
  const valueClassName = 'text-sm text-foreground';
  const channelPillClassName = (active: boolean) =>
    `inline-flex h-6 items-center rounded-full border px-2.5 text-xs font-medium ${
      active ? 'border-primary bg-primary text-primary-foreground' : 'border-muted bg-muted/70 text-muted-foreground'
    }`;
  const participantName =
    selectedTeilnehmer?.name ||
    trialRequestPerson?.companyName ||
    `${trialRequestPerson?.firstName || ''} ${trialRequestPerson?.lastName || ''}`.trim() ||
    '-';

  return (
    <WizardCard className="my-3">
      <div className="grid gap-4">
      <WizardSection title="Unterricht">
        <WizardKVGrid>
          <WizardKVItem label={getSubjectLabels().singular}>
            <span className={valueClassName}>{fachOptions.find((f) => f.id === fachId)?.name || '-'}</span>
          </WizardKVItem>
          <WizardKVItem label="Lehrer">
            <span className={valueClassName}>{teacherDisplayName || '-'}</span>
          </WizardKVItem>
          <WizardKVItem label="Datum">
            <span className={valueClassName}>{date ? formatDateWithWeekday(date) : '-'}</span>
          </WizardKVItem>
          <WizardKVItem label="Zeit">
            <span className={valueClassName}>
              {startTime || '-'} – {endTime || '-'}
            </span>
          </WizardKVItem>
        </WizardKVGrid>
        {selectedCourseId && selectedCourse ? (
          <div className="mt-3 border-t pt-3 text-sm">
            <div className="text-xs text-muted-foreground">Zugewiesener Kurs</div>
            <div className={valueClassName}>
              {selectedCourse.name} ({selectedCourse.dauer_min} Min)
            </div>
          </div>
        ) : null}
      </WizardSection>

      <WizardSection title="Ort">
        <WizardKVGrid>
          <WizardKVItem label="Standort">
            <span className={valueClassName}>{selectedStandort?.name || (selectedStandortId ? `#${selectedStandortId}` : '-')}</span>
          </WizardKVItem>
          <WizardKVItem label="Adresse">
            <span className={valueClassName}>{formatRoomAddress(rooms.find((r) => r.id === roomId) || null)}</span>
          </WizardKVItem>
          <WizardKVItem label="Raum">
            <span className={valueClassName}>{roomId ? rooms.find((r) => r.id === roomId)?.name || '-' : '-'}</span>
          </WizardKVItem>
        </WizardKVGrid>
      </WizardSection>

      <WizardSection title="Benachrichtigungen">
        <WizardKVGrid>
          <WizardKVItem label="Lehrer">
            <div className="flex flex-wrap items-center gap-2">
              <span className={valueClassName}>{teacherDisplayName || '-'}</span>
              <span className={channelPillClassName(!skipTeacherEmail)}>E-Mail</span>
              <span className={channelPillClassName(!skipTeacherSms)}>SMS</span>
            </div>
          </WizardKVItem>
          <WizardKVItem label="Teilnehmer">
            <div className="flex flex-wrap items-center gap-2">
              <span className={valueClassName}>{participantName || '-'}</span>
              <span className={channelPillClassName(!skipStudentEmail)}>E-Mail</span>
              <span className={channelPillClassName(!skipStudentSms)}>SMS</span>
            </div>
          </WizardKVItem>
        </WizardKVGrid>
      </WizardSection>

      <WizardSection title="Event-Historie">
        <div className="grid gap-4">
          <div>
            <div className="text-sm font-medium mb-2">Historieneintrag</div>
            <Textarea
              rows={5}
              value={createHistoryText}
              onChange={(e) => onChangeHistoryText(e.target.value)}
              placeholder="Historieneintrag…"
            />
            <div className="mt-2 text-xs text-muted-foreground">
              Dieser Text wird beim Eintragen als Historieneintrag gespeichert und kann hier bei Bedarf angepasst werden.
            </div>
          </div>

          {autoTaskPreview?.enabled ? (
            <div className="rounded-md border p-3 text-sm">
              <div className="text-xs text-muted-foreground mb-1">Aufgaben (Preview)</div>
              <div className="space-y-1">
                <div className="text-sm">{autoTaskPreview.description || 'Automatische Aufgabe aus Vorlage'}</div>
                <div className="text-xs text-muted-foreground">Fällig: {autoTaskPreview.dueAt || 'wird beim Speichern berechnet'}</div>
              </div>
            </div>
          ) : null}
        </div>
      </WizardSection>
      </div>
    </WizardCard>
  );
};

export default StepSummary;
